import { Close } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Popover,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { FormEvent, useContext, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SiteLookupContext from '../context/SiteLookupContext';
import { siteLookupTokens } from '../siteLookupTokens';
import NetworkPicker from './NetworkPicker';
import {
  Dealership,
  FilterCategory,
  FiltersDraft,
  MAX_FILTER_NETWORK_IDS,
  areSearchFiltersEqual,
  parseFiltersDraft,
  toFiltersDraft,
  withCategory,
} from './searchFilters';

type Props = {
  anchorEl: HTMLElement | null;
  category: FilterCategory;
  id: string;
  /** closes without applying: the draft is discarded */
  onClose: () => void;
  /** called once the applied filters include the draft, only while this popover is still open */
  onApplied: () => void;
};

const dealershipOptions: Dealership[] = ['all', 'exclude', 'only'];

/**
 * An empty native date input is not blank everywhere: Safari fills its segments with today's
 * date, which reads as a value the user never entered. While a date field is empty and not
 * focused, its native segments are hidden and a neutral placeholder is shown instead; focusing
 * it brings back the native segments for keyboard entry and the browser picker. The draft value
 * stays '' (no bound), so nothing changes in what is applied or sent.
 */
const emptyDateSx = (value: string, placeholder: string) =>
  value === ''
    ? {
        // `:valid` keeps a partially typed date (value '' but bad input) visible
        '& .MuiInputBase-root:not(:focus-within):has(input:valid)': {
          '& input': { color: 'transparent' },
          '& input::-webkit-datetime-edit': { opacity: 0 },
          '&::after': {
            content: JSON.stringify(placeholder),
            position: 'absolute',
            left: 14,
            right: 40,
            top: '50%',
            transform: 'translateY(-50%)',
            opacity: 0.42,
            overflow: 'hidden',
            pointerEvents: 'none',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          },
        },
      }
    : undefined;

/**
 * Edits one filter category. The draft starts from the applied filters each time the popover
 * opens, and nothing outside changes until a search with it succeeds.
 */
const FilterPopover = ({ anchorEl, category, id, onApplied, onClose }: Props) => {
  const { t } = useTranslation();
  const { breakpoints } = useTheme();
  const isNarrow = useMediaQuery(breakpoints.down('sm'));
  const { appliedFilters, applyFilters, networks, pendingRequest } = useContext(SiteLookupContext);

  const [draft, setDraft] = useState<FiltersDraft>(() => toFiltersDraft(appliedFilters));
  const [showErrors, setShowErrors] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [hasApplyFailed, setHasApplyFailed] = useState(false);

  const titleId = useId();
  const fieldId = useId();
  const firstFieldRef = useRef<HTMLInputElement>(null);

  // The user can close this popover, and open another one, while its search is pending:
  // the outcome must then neither close nor update anything.
  const isMountedRef = useRef(false);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Focus lands on the category's first field (the search for networks).
  useEffect(() => {
    // The popover content is mounted in a transition: wait for it.
    const frame = requestAnimationFrame(() => firstFieldRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, []);

  // Only this category is edited, on top of the applied filters (which are always valid).
  const { filters: parsedFilters, errors } = useMemo(() => parseFiltersDraft(draft), [draft]);
  const nextFilters = useMemo(() => withCategory(appliedFilters, parsedFilters, category), [appliedFilters, category, parsedFilters]);
  const hasErrors = Object.keys(errors).length > 0;
  const isUnchanged = !hasErrors && areSearchFiltersEqual(nextFilters, appliedFilters);
  const isSearching = isApplying || !!pendingRequest;

  const update = <K extends keyof FiltersDraft>(key: K, value: FiltersDraft[K]) => {
    setDraft((old) => ({ ...old, [key]: value }));
    setHasApplyFailed(false);
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (isUnchanged || isSearching) {
      return;
    }

    if (hasErrors) {
      setShowErrors(true);
      return;
    }

    setIsApplying(true);
    setHasApplyFailed(false);
    const outcome = await applyFilters(nextFilters);

    if (!isMountedRef.current) {
      return;
    }

    setIsApplying(false);

    if (outcome === 'success') {
      onApplied();
    } else if (outcome === 'error') {
      setHasApplyFailed(true);
    }
  };

  // Draft only, like Clear selection: the opening-date filter is removed once applied.
  // The button disables itself: keep the focus in the popover so Esc still closes it.
  const clearDates = () => {
    firstFieldRef.current?.focus();
    setDraft((old) => ({ ...old, openedFrom: '', openedTo: '' }));
    setHasApplyFailed(false);
  };

  const errorText = (key: keyof typeof errors) => (showErrors && errors[key] ? t(errors[key]) : undefined);

  const renderFields = () => {
    switch (category) {
      case 'network':
        return (
          <NetworkPicker
            catalogue={networks}
            // shown as soon as the selection is too large, while the user is selecting
            error={errors.networkIds && t(errors.networkIds, { max: MAX_FILTER_NETWORK_IDS })}
            onChange={(networkIds) => update('networkIds', networkIds)}
            searchRef={firstFieldRef}
            value={draft.networkIds}
          />
        );
      case 'minimumPorts':
        return (
          <TextField
            id={`${fieldId}-ports`}
            error={!!errorText('minimumPortsPerStation')}
            helperText={errorText('minimumPortsPerStation')}
            inputRef={firstFieldRef}
            label={t('siteLookup.filters.minimumPorts')}
            onChange={(event) => update('minimumPortsPerStation', event.target.value)}
            placeholder={t('siteLookup.filters.noMinimum')}
            size="small"
            value={draft.minimumPortsPerStation}
            slotProps={{ htmlInput: { inputMode: 'numeric' }, inputLabel: { shrink: true } }}
          />
        );
      case 'minimumPower':
        return (
          <TextField
            id={`${fieldId}-power`}
            error={!!errorText('minimumPowerKwPerPort')}
            helperText={errorText('minimumPowerKwPerPort')}
            inputRef={firstFieldRef}
            label={t('siteLookup.filters.minimumPower')}
            onChange={(event) => update('minimumPowerKwPerPort', event.target.value)}
            placeholder={t('siteLookup.filters.noMinimum')}
            size="small"
            value={draft.minimumPowerKwPerPort}
            slotProps={{
              htmlInput: { inputMode: 'decimal' },
              inputLabel: { shrink: true },
              input: {
                endAdornment: (
                  <Typography color="text.secondary" fontSize={14}>
                    {t('siteLookup.filters.kw')}
                  </Typography>
                ),
              },
            }}
          />
        );
      case 'openingDate':
        return (
          <Box display="flex" flexDirection="column" alignItems="flex-end" gap={1}>
            <Box display="grid" gap={2} gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} width="100%">
              <TextField
                id={`${fieldId}-from`}
                inputRef={firstFieldRef}
                label={t('siteLookup.filters.from')}
                onChange={(event) => update('openedFrom', event.target.value)}
                size="small"
                type="date"
                value={draft.openedFrom}
                slotProps={{ inputLabel: { shrink: true } }}
                sx={emptyDateSx(draft.openedFrom, t('siteLookup.filters.anyDate'))}
              />
              <TextField
                id={`${fieldId}-to`}
                error={!!errorText('openedTo')}
                helperText={errorText('openedTo')}
                label={t('siteLookup.filters.to')}
                onChange={(event) => update('openedTo', event.target.value)}
                size="small"
                type="date"
                value={draft.openedTo}
                slotProps={{ inputLabel: { shrink: true } }}
                sx={emptyDateSx(draft.openedTo, t('siteLookup.filters.anyDate'))}
              />
            </Box>
            <Button size="small" disabled={!draft.openedFrom && !draft.openedTo} onClick={clearDates}>
              {t('siteLookup.filters.clearDates')}
            </Button>
          </Box>
        );
      case 'dealership':
        return (
          <RadioGroup aria-labelledby={titleId} onChange={(_, value) => update('dealership', value as Dealership)} row value={draft.dealership}>
            {dealershipOptions.map((option) => (
              <FormControlLabel
                key={option}
                control={<Radio size="small" inputRef={option === draft.dealership ? firstFieldRef : undefined} />}
                label={t(`siteLookup.filters.dealership.${option}`)}
                value={option}
              />
            ))}
          </RadioGroup>
        );
    }
  };

  return (
    <Popover
      id={id}
      anchorEl={anchorEl}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      disableRestoreFocus
      marginThreshold={8}
      onClose={onClose}
      open={!!anchorEl}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      slotProps={{
        paper: {
          role: 'dialog',
          'aria-labelledby': titleId,
          sx: {
            border: `1px solid ${siteLookupTokens.color.border}`,
            borderRadius: '12px',
            boxShadow: '0 12px 32px rgb(17 24 39 / 14%)',
            display: 'flex',
            flexDirection: 'column',
            marginTop: 0.75,
            maxHeight: 'calc(100vh - 16px)',
            maxWidth: 'calc(100vw - 16px)',
            overflow: 'hidden',
            // full width on narrow screens, within the viewport
            width: isNarrow ? 'calc(100vw - 16px)' : category === 'network' || category === 'openingDate' ? 380 : 320,
          },
        },
      }}
    >
      <Box
        component="form"
        noValidate
        onSubmit={handleSubmit}
        aria-labelledby={titleId}
        aria-busy={isSearching}
        sx={{ display: 'flex', flexDirection: 'column', maxHeight: 'inherit', minHeight: 0 }}
      >
        <Box display="flex" alignItems="center" gap={1} paddingLeft={2} paddingRight={1} paddingTop={1}>
          <Typography id={titleId} component="h2" color={siteLookupTokens.color.text.primary} fontSize={15} fontWeight={700} flexGrow={1}>
            {t(`siteLookup.filters.titles.${category}`)}
          </Typography>
          <IconButton aria-label={t('siteLookup.filters.close')} onClick={onClose} size="small">
            <Close fontSize="small" />
          </IconButton>
        </Box>

        <Box flex="1 1 auto" minHeight={0} overflow="auto" paddingX={2} paddingTop={1.5} paddingBottom={2}>
          {renderFields()}
        </Box>

        <Box borderTop="1px solid" borderColor={siteLookupTokens.color.border} paddingX={2} paddingY={1.5} display="flex" flexDirection="column" gap={1}>
          {hasApplyFailed && (
            <Alert severity="error" sx={{ paddingY: 0 }}>
              {t('siteLookup.filters.applyError')}
            </Alert>
          )}
          <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
            <Typography color={siteLookupTokens.color.text.muted} fontSize={12}>
              {t('siteLookup.filters.appliesTo')}
            </Typography>
            <Button
              disabled={isUnchanged || isSearching}
              disableElevation
              startIcon={isSearching ? <CircularProgress color="inherit" size={16} /> : undefined}
              sx={{
                borderRadius: siteLookupTokens.size.controlRadius,
                flexShrink: 0,
                px: 2,
                '&:not(.Mui-disabled)': { backgroundColor: siteLookupTokens.color.primary },
                '&:not(.Mui-disabled):hover': { backgroundColor: siteLookupTokens.color.link },
              }}
              type="submit"
              variant="contained"
            >
              {t('siteLookup.filters.apply')}
            </Button>
          </Box>
        </Box>
      </Box>
    </Popover>
  );
};

export default FilterPopover;
